import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedRef, useAnimatedScrollHandler, useSharedValue, } from 'react-native-reanimated';
import { useScrollToTop } from '@react-navigation/native';
import { useAppSelector } from 'store/hooks';
import { AppView } from 'design-system/AppView';
import { NavigationHeader } from 'design-system/NavigationHeader';
import Section from 'design-system/Section';
import Text from 'design-system/Text';
import { rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useIsTablet from 'hooks/useIsTablet';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStatusBar from 'hooks/useStatusBar';
import CheckCreditScoreCard from 'features/credit-score/components/CheckCreditScoreCard';
import CreditScoreNextBestActionCard from 'features/credit-score/components/CreditScoreNextBestActionCard';
import CreditScoreTileSection from 'features/credit-score/components/CreditScoreTileSection';
import { selectIsCreditScoreAvailable, selectIsCreditScoreRegistered, } from 'features/credit-score/selectors';
import AvatarFeed from 'features/feed/components/avatar-feed/AvatarFeed';
import RentReportingSection from 'features/feed/components/rent-reporting/RentReportingSection';
import { useIsMainAppWalkthroughV6Active } from 'features/feed/components/walkthrough/hooks/useIsMainAppWalkthroughV6Active';
import { useShowWalkthroughV6 } from 'features/feed/components/walkthrough/hooks/useShowWalkthroughV6';
import MainWalkthroughHeaderOverlay from 'features/feed/components/walkthrough/main/MainWalkthroughHeaderOverlay';
import useWalkthroughLayout from 'features/feed/components/walkthrough/useWalkthroughLayout';
import Walkthrough from 'features/feed/components/walkthrough/Walkthrough';
import { selectHasCurrentRentReporting, selectHasRentReporting, } from 'features/rent-reporting/selectors';
import { selectTabsPriority } from 'reducers/selectors/organiseTabs';
import { isWeb } from '../../constants';
import BorrowHeaderRight from './components/BorrowHeaderRight';
import ImproveCreditScoreCard from './components/ImproveCreditScoreCard';
import LoanItemsList from './components/LoanItemsList';
const strings = {
    headerTitle: 'Track and learn how to improve your credit score',
    headerSubtitle: 'Use our tools to help you build a better credit history',
};
const BoostCreditHeader = () => (<View style={styles.boostCreditHeader}>
    <View style={styles.boostCreditText}>
      <Text Text-24 centered>
        {strings.headerTitle}
      </Text>
      <Text TextThin-16 Secondary centered>
        {strings.headerSubtitle}
      </Text>
    </View>
  </View>);
const BorrowTab = () => {
    const isTablet = useIsTablet();
    const scrollY = useSharedValue(0);
    const onScroll = useAnimatedScrollHandler((e) => {
        scrollY.value = e.contentOffset.y;
    });
    useStatusBar('dark-content', false);
    const scrollViewRef = useAnimatedRef();
    useScrollToTop(scrollViewRef);
    const isMainWalkthroughV6Active = useIsMainAppWalkthroughV6Active();
    const showWalkthroughV6 = useShowWalkthroughV6();
    const { itemPositions, scrollHeight, scrollLayoutHeight, onContentSizeChange, onLayout, onScrollLayout, } = useWalkthroughLayout();
    const { paddingHorizontalStyle } = useAppFrameDimensions(true);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle, !isTablet ? styles.container : undefined);
    const { hideTabNavigation } = useAppSelector(selectTabsPriority);
    const headerLeft = useMemo(() => (isTablet && !hideTabNavigation) || isWeb ? null : (<View style={styles.avatar} pointerEvents={isMainWalkthroughV6Active ? 'none' : 'auto'}>
          <AvatarFeed />
        </View>), [hideTabNavigation, isMainWalkthroughV6Active, isTablet]);
    const isCreditScoreAvailable = useAppSelector(selectIsCreditScoreAvailable);
    const isCreditScoreRegistered = useAppSelector(selectIsCreditScoreRegistered);
    const hasRentReporting = useAppSelector(selectHasRentReporting);
    const hasRentReportingRegistered = useAppSelector(selectHasCurrentRentReporting);
    const showBoostCreditHeader = isCreditScoreAvailable &&
        hasRentReporting &&
        !isCreditScoreRegistered &&
        !hasRentReportingRegistered;
    const onRentReportingSectionLayout = useCallback((event) => {
        onLayout('rentReporting', event);
    }, [onLayout]);
    return (<AppView withFrame={false}>
      <NavigationHeader isInTab title="Credit" hideTitleOnScroll={showBoostCreditHeader} scrollY={scrollY} leftContent={headerLeft} rightContent={<BorrowHeaderRight />} isWalkthroughActive={isMainWalkthroughV6Active} overlay={isMainWalkthroughV6Active ? MainWalkthroughHeaderOverlay : undefined}/>
      <Animated.ScrollView ref={scrollViewRef} onScroll={onScroll} scrollEventThrottle={16} onLayout={onScrollLayout} scrollEnabled={!isMainWalkthroughV6Active} onContentSizeChange={onContentSizeChange} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} style={styles.contentPaddingTop}>
        {!!showBoostCreditHeader && <BoostCreditHeader />}
        <CheckCreditScoreCard />
        <CreditScoreTileSection />
        <CreditScoreNextBestActionCard />
        <View onLayout={onRentReportingSectionLayout}>
          <RentReportingSection sectionTitle="Credit report boosting actions"/>
        </View>
        <Section title="Our offers" style={styles.section}>
          <LoanItemsList />
        </Section>
        {!isWeb && <ImproveCreditScoreCard />}
        {!!showWalkthroughV6 && (<Walkthrough isInTab scrollRef={scrollViewRef} scrollHeight={scrollHeight} itemPositions={itemPositions} scrollLayoutHeight={scrollLayoutHeight}/>)}
      </Animated.ScrollView>
    </AppView>);
};
const styles = StyleSheet.create({
    container: {
        paddingBottom: SCREEN_BOTTOM_PADDING,
    },
    contentPaddingTop: {
        paddingTop: rem(8),
    },
    avatar: {
        position: 'absolute',
        left: rem(16),
    },
    section: {
        marginTop: rem(8),
    },
    boostCreditHeader: {
        padding: rem(24),
        alignItems: 'center',
    },
    boostCreditText: {
        gap: rem(8),
        maxWidth: rem(320),
    },
});
export default BorrowTab;
